import React, { FC, memo, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Dropdown, Menu, Slider } from 'udesk-ui';
import { PlayCircleFilled, PauseCircleFilled } from '@udesk/icons';
import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';

const PlaySpeedMap = {
    0.5: 0.5,
    1: 1,
    1.2: 1.2,
    1.5: 1.5,
    2: 2,
};

const PlaySpeedMapOptions = [
    {
        value: PlaySpeedMap['0.5'],
        label: '0.5x',
    },
    {
        value: PlaySpeedMap['1'],
        label: '1.0x',
    },
    {
        value: PlaySpeedMap['1.2'],
        label: '1.2x',
    },
    {
        value: PlaySpeedMap['1.5'],
        label: '1.5x',
    },
    {
        value: PlaySpeedMap['2'],
        label: '2.0x',
    },
];

interface AudioProps {
    src?: string;
    dialogueRef?: any;
    scoreAnalysis?: any[];
    AudioRef?: any;
}

const Audio: FC<AudioProps> = memo((props) => {
    const { src, dialogueRef, scoreAnalysis, AudioRef } = props;

    const audioRef = useRef<HTMLAudioElement>(null);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const onCanPlay = (e) => {
        setDuration(e.target.duration);
    };

    const onEnded = () => {
        setIsPlaying(false);
    };

    const onPlay = () => {
        setIsPlaying(true);
        audioRef.current?.play?.();
    };

    const onPause = () => {
        setIsPlaying(false);
        audioRef.current?.pause?.();
    };

    const [curPos, setCurPos] = useState(0);

    const onTimeChange = (v) => {
        setCurPos(v);
        if (audioRef.current) {
            audioRef.current.currentTime = (v / 100) * duration;
        }
    };

    const [curIdx, setCurIdx] = useState(-1);

    const onTimeUpdate = (e) => {
        const moment = e.target.currentTime;
        setCurPos((moment / duration) * 100);

        scoreAnalysis?.reduce((pre, cur, idx) => {
            const n = (cur.duration || 0) / 1000;
            if (pre + n >= moment && pre < moment) {
                if (idx !== curIdx) {
                    dialogueRef.current?.scrollTo?.(idx);
                    setCurIdx(idx);
                }
            }
            return pre + n;
        }, 0);

        if (endTime && endTime <= e.target.currentTime) {
            onPause();
            setEndTime(undefined);
        }
    };

    const { curTime, totalTime } = useMemo(() => {
        const totalTime = Udesk.utils.date.formatDuration(duration);
        const curTime = Udesk.utils.date.formatDuration((curPos / 100) * duration);

        return {
            totalTime,
            curTime,
        };
    }, [curPos, duration]);

    const [playSpeed, setPlaySpeed] = useState<number>(PlaySpeedMap[1]);

    const onPlaySpeedChange = (speed: number) => {
        if (audioRef.current) {
            audioRef.current.playbackRate = speed;
        }

        setPlaySpeed(speed);
    };

    const [endTime, setEndTime] = useState<number>();

    useImperativeHandle(
        AudioRef,
        () => {
            return {
                duration,
                playSpecificTimePeriod: (startTime: number, endTime: number, duration: number) => {
                    if (audioRef.current) {
                        audioRef.current.currentTime = startTime;
                        onPlay();
                    }

                    setEndTime(endTime);
                },
            };
        },
        [duration]
    );

    if (!src) {
        return null;
    }

    return (
        <div style={{ height: '100%' }}>
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    gap: 8,
                }}
            >
                <div style={{ fontSize: 24, color: '#1b6dff' }}>
                    {isPlaying ? (
                        <PauseCircleFilled onClick={onPause} />
                    ) : (
                        <PlayCircleFilled onClick={onPlay} />
                    )}
                </div>

                <div style={{ color: 'rgba(0, 0, 0, .45)' }}>{curTime}</div>

                <Slider
                    tooltipVisible={false}
                    style={{ flex: 1 }}
                    value={curPos}
                    onChange={onTimeChange}
                />

                <div style={{ color: 'rgba(0, 0, 0, .45)' }}>{totalTime}</div>

                <Dropdown
                    trigger={['click']}
                    overlay={
                        <Menu onClick={(e) => onPlaySpeedChange(+e.key)}>
                            {PlaySpeedMapOptions.map((o) => (
                                <Menu.Item key={o.value}>{o.label}</Menu.Item>
                            ))}
                        </Menu>
                    }
                >
                    <div style={{ fontSize: 12, color: '#1b6dff', textAlign: 'center' }}>
                        <div>倍速</div>
                        <div>{PlaySpeedMapOptions.find((o) => o.value === playSpeed)?.label}</div>
                    </div>
                </Dropdown>
            </div>

            <audio
                ref={audioRef}
                src={src}
                onTimeUpdate={onTimeUpdate}
                onCanPlay={onCanPlay}
                onEnded={onEnded}
                hidden
            >
                {/* 浏览器不支持音频播放。 */}
                {
                    UdeskLocales['current'].pages.coach.learningCenter.record.detail.components
                        .audio.index.theBrowserDoesNotSupportAudioPlayback
                }
            </audio>
        </div>
    );
});

export default Audio;
